import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/customer/DashboardLayout";
import {
  customerAddressService,
  Address,
} from "../../services/customer.address.service";
import styles from "../../styles/app/AddressPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";
import AddressCard from "../../components/customer/AddressCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faAddressBook } from "@fortawesome/free-solid-svg-icons";
import AddressCreationModal from "../../components/common/AddressCreationModal";
import EditAddressModal from "../../components/common/EditAddressModal";
import DashboardCard from "../../components/common/DashboardCard";
import Swal from "sweetalert2";
import { countryService, Country } from "../../services/country.service";

const AddressPage: React.FC = () => {
  const { customerData } = useAuth();
  const [viewMode, setViewMode] = useState<'origin' | 'destination'>('origin');
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [sendersList, setSendersList] = useState<Country[]>([]);
  const [receiversList, setReceiversList] = useState<Country[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState<Address | null>(null);

  useEffect(() => {
    const fetchCountryLists = async () => {
      try {
        const { senders, receivers } = await countryService.getCountryLists();
        setSendersList(senders);
        setReceiversList(receivers);
        if (viewMode === 'origin' && senders.length > 0) {
          setSelectedCountry(senders[0].iso2Country);
        } else if (viewMode === 'destination' && receivers.length > 0) {
          setSelectedCountry(receivers[0].iso2Country);
        }
      } catch (err) {
        console.error("Error fetching country lists in AddressPage:", err);
      }
    };
    fetchCountryLists();
  }, []);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const fetchedAddresses = await customerAddressService.getAddresses();
      setAddresses(fetchedAddresses.all || []);
    } catch (err) {
      console.error("Error fetching addresses:", err);
      setError("Error al cargar las direcciones.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerData) {
      fetchAddresses();
    }
  }, [customerData]);

  const handleToggle = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleAddAddressClick = () => {
    setShowAddModal(true);
    setExpandedIndex(null);
  };

  const handleEditAddressClick = (address: Address) => {
    setAddressToEdit(address);
    setShowEditModal(true);
    setExpandedIndex(null);
  };

  const handleAddressAdded = async () => {
    setShowAddModal(false);
    await fetchAddresses();
  };

  const handleAddressUpdated = async () => {
    setShowEditModal(false);
    setAddressToEdit(null);
    await fetchAddresses();
  };

  const handleCancelAddAddress = () => {
    setShowAddModal(false);
  };

  const handleCancelEditAddress = () => {
    setShowEditModal(false);
    setAddressToEdit(null);
  };

  const handleDeleteAddress = async (addressId: number) => {
    Swal.fire({
      title: "¿Estás seguro?",
      text: "No podrás revertir esto.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminarlo",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await customerAddressService.deleteAddress(
            addressId
          );
          if (response.success) {
            Swal.fire(
              "¡Eliminado!",
              "La dirección ha sido eliminada.",
              "success"
            );
            await fetchAddresses();
          } else {
            Swal.fire(
              "Error",
              response.error || "Hubo un problema al eliminar la dirección.",
              "error"
            );
          }
        } catch (error) {
          console.error("Error al eliminar la dirección:", error);
          Swal.fire(
            "Error",
            "Ocurrió un error inesperado al eliminar la dirección.",
            "error"
          );
        }
      }
    });
  };

  const handleSetDefaultAddress = async (addressId: number) => {
    try {
      const response = await customerAddressService.setDefaultAddress(addressId);
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "¡Dirección Predeterminada!",
          text: "Se ha actualizado tu dirección predeterminada.",
          timer: 2000,
          showConfirmButton: false,
        });
        await fetchAddresses();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.error || "No se pudo actualizar la dirección predeterminada.",
        });
      }
    } catch (err) {
      console.error("Error setting default address:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error inesperado al actualizar la dirección.",
      });
    }
  };

  if (!customerData) {
    return (
      <DashboardLayout>
        <div>Cargando perfil...</div>
      </DashboardLayout>
    );
  }

  const currentCount = addresses.filter(
    (addr) =>
      addr.isO2Country === selectedCountry &&
      (viewMode === 'origin' ? addr.type === 'O' : addr.type === 'D')
  ).length;
  const isLimitReached = currentCount >= 5;

  return (
    <DashboardLayout>
      <DashboardCard
        icon={faAddressBook}
        title="Mis direcciones"
        description="Aquí puedes ver y gestionar tus direcciones de envío."
        actions={
          <button
            className={btnStyles.primary}
            onClick={handleAddAddressClick}
            disabled={isLimitReached}
            title={isLimitReached ? "Límite de 5 direcciones alcanzado para este país y tipo" : ""}
            style={isLimitReached ? { opacity: 0.5, cursor: "not-allowed" } : {}}
          >
            Agregar Dirección
          </button>
        }
        showVerificationAlerts={true}
      />
      <div className={styles.addressPageMain}>
        {loading && <p>Cargando direcciones...</p>}

        {!loading && error && <p className={styles.errorMessage}>{error}</p>}

        {!loading && !error && (
          <>
            {/* Switch Row with Select all the way to the right */}
            <div className={styles.filterRow}>
              <div className={styles.viewToggleContainer}>
                <button
                  className={`${styles.toggleBtn} ${viewMode === 'origin' ? styles.active : ''}`}
                  onClick={() => {
                    setViewMode('origin');
                    const defaultSender = sendersList[0]?.iso2Country || '';
                    setSelectedCountry(defaultSender);
                  }}
                >
                  Orígenes
                </button>
                <button
                  className={`${styles.toggleBtn} ${viewMode === 'destination' ? styles.active : ''}`}
                  onClick={() => {
                    setViewMode('destination');
                    const defaultReceiver = receiversList[0]?.iso2Country || '';
                    setSelectedCountry(defaultReceiver);
                  }}
                >
                  Destinos
                </button>
              </div>

              <div className={styles.countrySelectContainer}>
                <select
                  className={styles.filterSelect}
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                >
                  {(viewMode === 'origin' ? sendersList : receiversList).map((country) => (
                    <option key={country.id || country.iso2Country} value={country.iso2Country}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {addresses.filter(a => a.isO2Country === selectedCountry && (viewMode === 'origin' ? a.type === 'O' : a.type === 'D')).length === 0 ? (
              <p className={styles.noAddressesMessage}>
                No hay direcciones registradas para este país.
              </p>
            ) : (
              <div className={styles.addressPageGrid}>
                {addresses
                  .filter(a => a.isO2Country === selectedCountry && (viewMode === 'origin' ? a.type === 'O' : a.type === 'D'))
                  .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
                  .map((address, index) => (
                    <AddressCard
                      key={address.id || index}
                      address={address}
                      currentUser={customerData}
                      onDelete={handleDeleteAddress}
                      onEdit={handleEditAddressClick}
                      onSetDefault={handleSetDefaultAddress}
                    />
                  ))
                }
              </div>
            )}
          </>
        )}
      </div>

      <AddressCreationModal
        isOpen={showAddModal}
        onClose={handleCancelAddAddress}
        type={viewMode}
        initialCountry={selectedCountry}
        onSuccess={handleAddressAdded}
        allowTypeSelection={true}
        addresses={addresses}
      />

      <EditAddressModal
        isOpen={showEditModal}
        onClose={handleCancelEditAddress}
        address={addressToEdit}
        onSuccess={handleAddressUpdated}
      />
    </DashboardLayout>
  );
};

export default AddressPage;
